<template>
    <vx-box>
        <!-- Container queries: the table sits beside a sidebar on some pages, so its own width decides the layout. -->
        <div class="@container flex flex-col">
            <!-- card header -->
            <div
                class="pt-6 px-6 pb-6 flex gap-2 flex-col justify-between @xl:items-center @xl:flex-row @4xl:pb-0 @4xl:justify-between @4xl:items-center"
            >
                <span v-if="props.loading" class="text-base font-extrabold">Loading...</span>
                <span v-else class="text-base font-extrabold">
                    <slot name="tableTitle" :totalCount="totalCount">{{
                        pluralized('resource', totalCount, true)
                    }}</slot>
                </span>
                <vx-button
                    @click="exportData"
                    :loading="exporting"
                    :disabled="!totalCount"
                    size="sm"
                    color="muted-light"
                    data-test="export-btn"
                >
                    Export <font-awesome-icon widthAuto :icon="faFileArrowDown" />
                </vx-button>
            </div>

            <div v-if="sortableHeaders.length" class="flex px-6 pb-4 w-full gap-2 @4xl:hidden">
                <vx-select v-model="sortField" @update:modelValue="sort" :options="sortableHeaders"></vx-select>
                <vx-button
                    @click.prevent="
                        sortDir = sortDir === 'asc' ? 'desc' : 'asc';
                        sort();
                    "
                    type="button"
                    color="white"
                    size="lg"
                    shadow
                    data-test="sort-direction"
                    :aria-label="`Sort ${
                        sortDir === 'asc' ? 'ascending' : 'descending'
                    }. Press to toggle sort direction.`"
                >
                    <font-awesome-icon
                        fixedWidth
                        :icon="sortDir === 'asc' ? faArrowUpWideShort : faArrowDownWideShort"
                    />
                </vx-button>
            </div>

            <div class="grid gap-x-4 exportable-table-grid" :class="props.colClasses" role="table">
                <!-- table header -->
                <slot name="header" :sort="sort">
                    <exportable-table-row isHeader>
                        <template v-for="header in visibleHeaders" :key="header.field">
                            <vx-popover
                                v-if="header.popoverContent"
                                hover
                                arrow
                                placement="top"
                                class="max-w-fit"
                                role="columnheader"
                                :aria-sort="ariaSort(header)"
                                :data-test="`column-${header.field}`"
                            >
                                <component
                                    :is="header.sortable ? 'button' : 'span'"
                                    :type="header.sortable ? 'button' : undefined"
                                    :data-test="`header-${header.field}`"
                                    @click="sortData(header)"
                                    class="max-w-fit px-1 py-0.5 rounded-sm flex flex-row gap-2 items-center"
                                    :class="{
                                        'cursor-pointer hover:bg-slate-100 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500':
                                            header.sortable,
                                    }"
                                >
                                    {{ header.label }}
                                    <font-awesome-icon
                                        widthAuto
                                        v-if="header.sortable"
                                        :class="[
                                            'w-3',
                                            sortDir === null || sortField !== header.field
                                                ? 'text-slate-400'
                                                : 'text-slate-800',
                                        ]"
                                        :icon="
                                            sortDir === null || sortField !== header.field
                                                ? faAnglesUpDown
                                                : sortDir === 'asc'
                                                ? faArrowUpLong
                                                : faArrowDownLong
                                        "
                                    />
                                </component>

                                <template v-slot:content>{{ header.popoverContent }}</template>
                            </vx-popover>

                            <div
                                v-else
                                role="columnheader"
                                :aria-sort="ariaSort(header)"
                                :data-test="`column-${header.field}`"
                            >
                                <component
                                    :is="header.sortable ? 'button' : 'span'"
                                    :type="header.sortable ? 'button' : undefined"
                                    :data-test="`header-${header.field}`"
                                    @click="sortData(header)"
                                    class="max-w-fit px-1 py-0.5 rounded-sm flex flex-row gap-2 items-center"
                                    :class="{
                                        'cursor-pointer hover:bg-slate-100 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500':
                                            header.sortable,
                                    }"
                                >
                                    {{ header.label }}
                                    <font-awesome-icon
                                        widthAuto
                                        v-if="header.sortable"
                                        :class="[
                                            sortDir === null || sortField !== header.field
                                                ? 'text-slate-400'
                                                : 'text-slate-800',
                                        ]"
                                        :icon="
                                            sortDir === null || sortField !== header.field
                                                ? faAnglesUpDown
                                                : sortDir === 'asc'
                                                ? faArrowUpLong
                                                : faArrowDownLong
                                        "
                                    />
                                </component>
                            </div>
                        </template>
                    </exportable-table-row>
                </slot>

                <!-- table rows -->
                <slot v-if="!props.loading" name="rows" :visibleItems="visibleItems">
                    <exportable-table-row
                        v-for="item in visibleItems"
                        :key="item[props.rowKey]"
                        :class="{
                            'last:rounded-b-2xl': !canLoadMore,
                        }"
                    >
                        <template v-slot:rowTitle>
                            <exportable-table-row-title v-if="titleField">
                                <slot name="rowTitle" :item="item" :field="titleField" :value="item[titleField]">
                                    {{ item[titleField] }}
                                </slot>
                            </exportable-table-row-title>
                        </template>

                        <template v-slot:default>
                            <exportable-table-row-item
                                v-for="header in dataHeaders"
                                :key="`${item[props.rowKey]}-${header.field}`"
                                class="@4xl:justify-items-end"
                            >
                                <template v-slot:label>{{ header.label }}:</template>

                                <slot name="cell" :item="item" :field="header.field" :value="item[header.field]">
                                    {{ item[header.field] }}
                                </slot>
                            </exportable-table-row-item>
                        </template>

                        <template v-slot:buttons>
                            <slot name="buttons" :item="item"></slot>
                        </template>
                    </exportable-table-row>
                </slot>

                <div
                    v-if="props.loading || !totalCount"
                    class="justify-center flex col-span-full min-h-80 items-center bg-slate-100 rounded-b-2xl"
                    role="row"
                >
                    <span v-if="props.loading" role="cell"
                        ><vx-icon-loading class="w-4 h-4 animate-loading-spin"></vx-icon-loading
                    ></span>
                    <span v-else role="cell">No data yet</span>
                </div>
            </div>

            <!-- card footer -->
            <div v-if="!props.loading && canLoadMore" class="py-4 px-6 flex flex-row justify-between items-center">
                <div class="font-normal text-sky950 text-sm">{{ visibleItems.length }} / {{ totalCount }} items</div>

                <vx-button @click="loadMore()" size="sm" color="muted-light" data-test="load-more-btn">
                    Load More <font-awesome-icon widthAuto :icon="faLoader" />
                </vx-button>
            </div>
        </div>
    </vx-box>
</template>

<script setup>
import { VxBox, VxButton, VxIconLoading, VxPopover, VxSelect } from '@voxie/frontend-components';
import {
    faAnglesUpDown,
    faArrowDownLong,
    faArrowDownWideShort,
    faArrowUpLong,
    faArrowUpWideShort,
    faFileArrowDown,
    faLoader,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { pluralized } from '~/components/filters';
import { computed, onMounted, ref, useSlots } from 'vue';
import ExportableTableRowItem from '~/components/analytics/partial/exportable-table/ExportableTableRowItem.vue';
import ExportableTableRow from '~/components/analytics/partial/exportable-table/ExportableTableRow.vue';
import ExportableTableRowTitle from '~/components/analytics/partial/exportable-table/ExportableTableRowTitle.vue';
import { download, prepareCsv } from '~/utils/files';

// 'headers' property accepts an array of objects, an object can contain:
// required: 'field'[String]
// required if not hidden: 'label'[String]
// optional: 'sortable'[Boolean], 'popoverContent'[String], 'hidden'[Boolean], 'exportable'[Boolean]
// by default sortable=false, hidden=false, exportable=true
// 'hidden' - if true, than it doesn't show the field in the table
// 'exportable' - if false, than it doesn't export the field
const props = defineProps({
    headers: {
        type: Array,
        required: true,
    },
    filename: {
        type: String,
        default: 'voxie-table-data',
    },
    colClasses: {
        type: String,
    },
    perPage: {
        type: Number,
        default: 15,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    rowKey: {
        type: String,
        required: true,
    },
});

const slots = useSlots();

const list = defineModel();

const exporting = ref(false);

const page = ref(1);
const sortDir = ref(null);
const sortField = ref(null);

const totalCount = computed(() => list.value.length);
const visibleItems = computed(() => (list.value.length ? list.value.slice(0, page.value * props.perPage) : []));
const canLoadMore = computed(() => visibleItems.value.length < totalCount.value);
const titleField = computed(() => props.headers.find((header) => header.title === true)?.field);
const dataHeaders = computed(() => props.headers.filter((header) => header.title !== true && header.hidden !== true));
const visibleHeaders = computed(() => props.headers.filter((header) => header.hidden !== true));

const sortableHeaders = computed(() =>
    props.headers
        .filter((header) => header.sortable)
        .map((header) => {
            return {
                value: header.field,
                label: `Sort by: ${header.label}`,
            };
        })
);

const loadMore = () => {
    if (canLoadMore.value) {
        page.value++;
    }
};

const sort = () => {
    list.value = list.value.sort((a, b) => {
        const aVal = a[sortField.value];
        const bVal = b[sortField.value];

        if (aVal == null && bVal == null) {
            return 0;
        }

        if (aVal == null) {
            return sortDir.value === 'asc' ? -1 : 1;
        }

        if (bVal == null) {
            return sortDir.value === 'asc' ? 1 : -1;
        }

        // Compare numbers
        if (typeof aVal === 'number' && typeof bVal === 'number') {
            return sortDir.value === 'asc' ? aVal - bVal : bVal - aVal;
        }

        // Compare as strings
        return sortDir.value === 'asc'
            ? String(aVal).localeCompare(String(bVal))
            : String(bVal).localeCompare(String(aVal));
    });
};

const exportData = () => {
    if (exporting.value) {
        return;
    }

    exporting.value = true;

    const exportableHeaders = props.headers.filter((h) => h.exportable !== false);
    const fields = exportableHeaders.map((h) => h.field);
    const rows = list.value.map((item) =>
        exportableHeaders.map((header) => {
            const value = item[header.field];
            return header.exportFormatter ? header.exportFormatter(value) : value;
        })
    );
    const csv = prepareCsv(fields, rows);

    download(csv, props.filename, { type: 'text/csv;charset=utf-8;' });

    exporting.value = false;
};

const sortData = (header) => {
    if (!header.sortable) {
        return;
    }

    sortField.value = header.field;
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
    sort();
};

const ariaSort = (header) => {
    if (sortDir.value === null || sortField.value !== header.field) {
        return undefined;
    }

    return sortDir.value === 'asc' ? 'ascending' : 'descending';
};

onMounted(() => {
    sortField.value = sortableHeaders.value[0]?.value || null;
});

// Computed properties for styles
const tableContentCols = computed(() => dataHeaders.value.length);
const colsNumber = computed(() => (slots.buttons ? visibleHeaders.value.length + 1 : visibleHeaders.value.length));
// The title column takes the leftover width and wraps; every other column gets exactly the width its content needs.
const fullColumnsTemplate = computed(() =>
    slots.header || titleField.value
        ? `minmax(0, 1fr)${colsNumber.value > 1 ? ` repeat(${colsNumber.value - 1}, max-content)` : ''}`
        : `repeat(${colsNumber.value}, max-content)`
);
// Below the full layout the row is title | label/value block | buttons. The title and the block hug their content so
// the block follows the title instead of drifting to the far edge, and the last track takes the leftover width so the
// rows still span the table. The title's minimum is zero so it wraps when the row would otherwise overflow.
const stackedColumnsTemplate = computed(() => {
    const tracks = [
        slots.header || titleField.value ? 'minmax(0, max-content)' : null,
        slots.buttons ? 'max-content' : 'minmax(max-content, 1fr)',
        slots.buttons ? 'minmax(max-content, 1fr)' : null,
    ];

    return tracks.filter((track) => null !== track).join(' ');
});
</script>

<style scoped>
.exportable-table-grid {
    grid-template-columns: repeat(1, auto);

    :deep(.exportable-table-content) {
        grid-template-columns: auto 1fr;
    }
}

@container (width >= 36rem) {
    .exportable-table-grid {
        grid-template-columns: v-bind(stackedColumnsTemplate);

        /* The block's track may take the leftover width, so center the labels and values within it. */
        :deep(.exportable-table-content) {
            grid-template-columns: max-content max-content;
            justify-content: center;
        }
    }
}

@container (width >= 56rem) {
    .exportable-table-grid {
        grid-template-columns: v-bind(fullColumnsTemplate);

        :deep(.exportable-table-content) {
            grid-column: span v-bind(tableContentCols) / span v-bind(tableContentCols);
            grid-template-columns: subgrid;
            justify-content: normal;
        }
    }
}
</style>
